'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

export default function ConsumerElectronicsCaseStudy() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(43,98,239,0.06), transparent, rgb(8,11,18))' }} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-8">
          <Link href="/case-studies" className="inline-flex items-center gap-2 text-cobalt text-sm font-medium mb-8 hover:text-cobalt-glow transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m15 18-6-6 6-6" />
            </svg>
            Back to Case Studies
          </Link>
          <span className="text-cobalt font-mono text-sm tracking-widest uppercase mb-4 block">Consumer Electronics</span>
          <h1 className="font-display font-bold text-4xl lg:text-5xl text-silver-text leading-tight mb-6 max-w-4xl">
            Global Content Localization for Leading Consumer Electronics Brands
          </h1>
          <p className="text-lg text-silver-muted max-w-2xl leading-relaxed mb-10">
            Millions of words delivered annually across 30+ languages, with 100% on-time delivery for product launches across Asian and global markets.
          </p>
          {/* Stats bar */}
          <div className="flex flex-wrap gap-6">
            {[
              { value: 'Millions+', label: 'Words Annually' },
              { value: '30+', label: 'Languages' },
              { value: '100%', label: 'On-Time Delivery' },
              { value: 'Hundreds', label: 'Launch Programs' },
            ]?.map((stat) => (
              <div key={stat?.label} className="glow-card rounded-xl px-6 py-4 text-center min-w-[120px]">
                <div className="text-xl font-bold text-cobalt-glow">{stat?.value}</div>
                <div className="text-xs text-silver-muted mt-1">{stat?.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Content */}
      <section className="py-16 lg:py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main content */}
            <div className="lg:col-span-2 space-y-12">

              {/* Challenge */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">01</span>
                  The Challenge
                </h2>
                <p className="text-silver-muted leading-relaxed mb-4">
                  Global consumer electronics launches required multilingual content across product, marketing, support, and compliance materials while keeping tone, terminology, and release timing aligned across markets. Brands including Sony, Anker, Apple, Xiaomi, and Microsoft needed a partner capable of delivering at scale without compromising quality or missing launch windows.
                </p>
                <p className="text-silver-muted leading-relaxed">
                  The core challenge was managing rapid product launch timelines, maintaining brand voice consistency across 30+ languages, handling technical complexity in product documentation, and scaling globally without sacrificing accuracy.
                </p>
              </div>

              {/* Content Types */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">02</span>
                  Content Types Localized
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { title: 'Marketing Materials', desc: 'Campaign copy, product launches, brand messaging' },
                    { title: 'Product Listings', desc: 'Titles, descriptions, feature highlights, SEO content' },
                    { title: 'Instruction Manuals', desc: 'Technical documentation, setup guides, safety notices' },
                    { title: 'Support & FAQ Content', desc: 'Help center articles, troubleshooting guides' },
                    { title: 'Legal & Compliance', desc: 'Regulatory documentation, warranty terms, certifications' },
                    { title: 'In-App / UI Text', desc: 'Interface strings, notifications, onboarding flows' },
                  ]?.map((item) => (
                    <div key={item?.title} className="glow-card rounded-xl p-5">
                      <h3 className="font-semibold text-silver-text text-sm mb-1">{item?.title}</h3>
                      <p className="text-xs text-silver-muted">{item?.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Solution */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">03</span>
                  The Into23 Solution
                </h2>
                <p className="text-silver-muted leading-relaxed mb-6">
                  Into23 deployed a hybrid AI and human workflow combining machine translation with native linguist review, supported by centralized termbases, style guides, and specialized desktop publishing teams.
                </p>
                <div className="space-y-4">
                  {[
                    { step: '1', title: 'Content Analysis & Preparation', desc: 'Structured intake, file analysis, and terminology extraction to align with brand voice before translation begins.' },
                    { step: '2', title: 'Translation & Localization', desc: 'Hybrid AI-human workflows with native linguists specializing in consumer electronics terminology and regional market nuances.' },
                    { step: '3', title: 'Multilingual Quality Assurance', desc: 'Native linguist reviews, automated consistency checks, and terminology validation across all language pairs.' },
                    { step: '4', title: 'Formatting & Final Delivery', desc: 'Output in all required formats supporting SEO, digital platforms, and print-ready documentation.' },
                  ]?.map((item) => (
                    <div key={item?.step} className="flex gap-4 p-5 glow-card rounded-xl">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-bold">{item?.step}</div>
                      <div>
                        <h3 className="font-semibold text-silver-text text-sm mb-1">{item?.title}</h3>
                        <p className="text-xs text-silver-muted leading-relaxed">{item?.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Languages */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">04</span>
                  Language Coverage
                </h2>
                <p className="text-silver-muted leading-relaxed mb-5">
                  Strong focus on Asian languages with full global coverage across 30+ markets:
                </p>
                <div className="flex flex-wrap gap-2">
                  {['Chinese (Simplified)', 'Chinese (Traditional)', 'Japanese', 'Korean', 'Thai', 'Vietnamese', 'Indonesian', 'Malay', 'Hindi', 'Arabic', 'French', 'German', 'Spanish', 'Italian', 'Portuguese', 'Dutch', 'Russian', 'Turkish', 'Polish', 'Swedish', 'Danish', 'Norwegian', 'Finnish', 'Czech', 'Hungarian', 'Romanian', 'Greek', 'Hebrew', 'Bengali', 'Tagalog']?.map((lang) => (
                    <span key={lang} className="px-3 py-1.5 rounded-lg bg-cobalt/10 border border-cobalt/20 text-cobalt-glow text-xs font-mono">{lang}</span>
                  ))}
                </div>
              </div>

              {/* Results */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">05</span>
                  Results & Impact
                </h2>
                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  {[
                    { metric: 'Millions of words', detail: 'Translated annually across all content types' },
                    { metric: '100% on-time delivery', detail: 'For all product launch windows' },
                    { metric: 'Consistent brand voice', detail: 'Across 30+ languages and all markets' },
                    { metric: 'Flexible workflows', detail: 'Balancing speed, cost, and quality per project' },
                  ]?.map((r) => (
                    <div key={r?.metric} className="glow-card rounded-xl p-5 border-l-2 border-cobalt/40">
                      <div className="font-semibold text-cobalt-glow text-sm mb-1">{r?.metric}</div>
                      <div className="text-xs text-silver-muted">{r?.detail}</div>
                    </div>
                  ))}
                </div>
                <blockquote className="border-l-2 border-cobalt/40 pl-5 py-2">
                  <p className="text-silver-muted italic text-sm leading-relaxed mb-2">"Rapid turnaround with consistent quality across all markets."</p>
                  <cite className="text-cobalt-glow text-xs not-italic">Client feedback, Leading consumer electronics brands</cite>
                </blockquote>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="glow-card rounded-xl p-6">
                <h3 className="font-semibold text-silver-text mb-4">Project Details</h3>
                <dl className="space-y-3 text-sm">
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Industry</dt><dd className="text-silver-text">Consumer Electronics</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Clients</dt><dd className="text-silver-text">Sony, Anker, Apple, Xiaomi, Microsoft</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Languages</dt><dd className="text-silver-text">30+ languages</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Delivery Model</dt><dd className="text-silver-text">Annual program + launch-based</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Approach</dt><dd className="text-silver-text">Hybrid AI + human workflow</dd></div>
                </dl>
              </div>

              <div className="glow-card rounded-xl p-6">
                <h3 className="font-semibold text-silver-text mb-3">Key Capabilities Used</h3>
                <ul className="space-y-2 text-sm text-silver-muted">
                  {['AI-assisted translation', 'Native linguist review', 'Centralized termbases', 'Style guide management', 'Desktop publishing', 'SEO localization', 'Multilingual QA']?.map((cap) => (
                    <li key={cap} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-cobalt-glow flex-shrink-0" />
                      {cap}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="glow-card rounded-xl p-6">
                <h3 className="font-semibold text-silver-text mb-3">Ready to Scale?</h3>
                <p className="text-sm text-silver-muted mb-4">Talk to our team about your multilingual content program.</p>
                <Link href="/get-a-quote">
                  <span className="block text-center px-4 py-3 bg-cobalt hover:bg-cobalt-glow text-white text-sm font-semibold rounded-lg transition-all">
                    Get a Quote
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
